import { Box } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from 'widgets/ComposableReportWidget';
import {
    buildSpendRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type { SpendSupplierRowDto } from 'entities/ReportSpendSummary';

const PIE_MAX_ITEMS = 5;
const TABLE_INITIAL_LIMIT = 5;

type SpendSummarySuppliersTableColumnId =
    | 'supplier'
    | 'orders'
    | 'items'
    | 'totalSpend'
    | 'spend'
    | 'percentOfTotal'
    | 'percentSpend';

function supplierToWidgetItem(dto: SpendSupplierRowDto): ReportWidgetItem {
    return {
        id: dto.supplierId,
        label: dto.supplierName,
        primaryValue: toNumber(dto.totalSpend),
        secondaryValues: {
            label: dto.supplierName,
            orderCount: dto.orderCount,
            itemCount: dto.itemCount,
            totalSpend: dto.totalSpend,
            percentOfTotal: dto.percentOfTotal,
        },
        meta: { supplierId: dto.supplierId, supplierName: dto.supplierName },
    };
}

interface SpendSummarySuppliersSectionProps {
    bySupplier: SpendSupplierRowDto[];
    totalSpend?: string;
    loading?: boolean;
    disableChartAnimation?: boolean;
    filtersKey?: string;
    showBottomBorder?: boolean;
    tableOnly?: boolean;
    tableColumnIds?: SpendSummarySuppliersTableColumnId[];
    onSupplierSelect?: (args: {
        supplierId: string;
        supplierName?: string;
    }) => void;
}

export const SpendSummarySuppliersSection = memo(
    ({
        bySupplier,
        totalSpend,
        loading = false,
        disableChartAnimation = false,
        filtersKey,
        showBottomBorder = true,
        tableOnly = false,
        tableColumnIds,
        onSupplierSelect,
    }: SpendSummarySuppliersSectionProps) => {
        const { t } = useTranslation();

        const items: ReportWidgetItem[] = useMemo(() => {
            const sorted = [...bySupplier]
                .filter((dto) => toNumber(dto.totalSpend) > 0)
                .sort((a, b) => toNumber(b.totalSpend) - toNumber(a.totalSpend))
                .map(supplierToWidgetItem);

            if (tableOnly) {
                return sorted;
            }

            return augmentItemsForTopNOther({
                items: sorted,
                topCount: PIE_MAX_ITEMS,
                otherChartItemId: 'spend-summary-suppliers-other',
            });
        }, [bySupplier, tableOnly]);

        const chartItems: ReportWidgetItem[] | undefined = useMemo(() => {
            if (tableOnly || items.length <= PIE_MAX_ITEMS) return undefined;

            return buildTopNChartItemsWithOther({
                items,
                totalAmount: totalSpend,
                topCount: PIE_MAX_ITEMS,
                otherItemId: 'spend-summary-suppliers-other',
                otherLabel: t('Other'),
            });
        }, [items, t, totalSpend, tableOnly]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                if (payload.item.meta?.isAggregatedOther) return;
                const supplierId = payload.item.meta?.supplierId;

                if (typeof supplierId !== 'string') return;

                onSupplierSelect?.({
                    supplierId,
                    supplierName: payload.item.label,
                });
            },
            [onSupplierSelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SpendSummarySuppliersTableColumnId,
                ReportWidgetColumnConfig
            > = {
                supplier: {
                    id: 'supplier',
                    label: t('spendSummary.supplier'),
                    valueKey: 'label',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    minWidth: 200,
                    allowWrap: true,
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                    align: 'right',
                },
                items: {
                    id: 'items',
                    label: t('Items'),
                    valueKey: 'itemCount',
                    format: 'number',
                    align: 'right',
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('spendSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                spend: {
                    id: 'spend',
                    label: t('spendSummary.spend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                percentOfTotal: {
                    id: 'percentOfTotal',
                    label: t('spendSummary.percentOfTotal'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
                percentSpend: {
                    id: 'percentSpend',
                    label: t('spendSummary.percentSpend'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
            };

            const defaultOrder: SpendSummarySuppliersTableColumnId[] = [
                'supplier',
                'orders',
                'items',
                'totalSpend',
                'percentSpend',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                if (args.aggregation?.kind === 'other') {
                    const entry = args.payload?.[0];
                    const aggregatedValue = toNumber(
                        entry?.value as number | string | null,
                    );

                    return [
                        ...args.aggregation.headerRows,
                        buildSpendRow({
                            id: 'spend',
                            label: t('spendSummary.spend'),
                            amount: aggregatedValue,
                            total: args.total,
                            valueFormatter: args.valueFormatter,
                            valueColor: entry?.color ?? 'metrics.base',
                        }),
                    ];
                }

                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const rows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 8,
                    },
                    buildSpendRow({
                        id: 'spend',
                        label: t('spendSummary.spend'),
                        amount: toNumber(item.primaryValue),
                        percent: item.secondaryValues?.percentOfTotal,
                        total: args.total,
                        valueFormatter: args.valueFormatter,
                        valueColor: resolved.color ?? 'metrics.base',
                    }),
                ];

                return rows;
            };

            return {
                widgetId: 'spend-summary-suppliers',
                title: t('spendSummary.suppliersSectionTitle'),
                modelLabel: t('spendSummary.modelLabelSuppliers'),
                layout: tableOnly ? 'stacked' : 'sideBySide',
                visualization: {
                    enabled: !tableOnly,
                    type: 'donut',
                    options: {
                        palette: 'neutral',
                        showLegend: false,
                        valueFormatter: formatReportCurrency,
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: TABLE_INITIAL_LIMIT,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        showColorSwatchInLabelColumn: tableOnly
                            ? undefined
                            : 'label',
                        loadMoreVariant: 'link',
                    },
                },
                onRowClick: onSupplierSelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onSupplierSelect
                        ? handleInteraction
                        : undefined,
            };
        }, [t, tableColumns, tableOnly, onSupplierSelect, handleInteraction]);

        return (
            <Box
                data-testclass="spend-summary-suppliers-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    key={filtersKey}
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SpendSummarySuppliersSection.displayName = 'SpendSummarySuppliersSection';
